<script setup>
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { DateTime } from 'luxon'
import { useRouter } from 'vue-router'
import { Notificaciones as api } from '../Api/API_Concentrador'
import { notificationBody, notificationIcon, notificationPath, notificationResource, notificationTime, notificationTitle } from '../Utils/notifications'

const router = useRouter()
const notificaciones = inject('notificaciones', ref([]))
const dropdown = ref(null)
const abierto = ref(false)
const filtro = ref('todas')
const error = ref('')
const noLeidas = computed(() => notificaciones.value.filter((n) => !n.readAt).length)
const filtradas = computed(() => notificaciones.value.filter((n) => {
    if (filtro.value === 'ventas') return /order|shipment|shipping|message|payment/.test(n.topic?.toLowerCase() ?? '')
    if (filtro.value === 'publicaciones') return /item|product|price|stock|catalog/.test(n.topic?.toLowerCase() ?? '')
    return true
}))
const esHoy = (n) => {
    const fecha = DateTime.fromISO(n.processedAt || n.sent || n.received || '')
    return fecha.isValid && fecha.hasSame(DateTime.local(), 'day')
}
const hoy = computed(() => filtradas.value.filter(esHoy))
const anteriores = computed(() => filtradas.value.filter((n) => !esHoy(n)))

function cerrarFuera(event) {
    if (abierto.value && dropdown.value && !dropdown.value.contains(event.target)) abierto.value = false
}
onMounted(() => window.addEventListener('click', cerrarFuera))
onUnmounted(() => window.removeEventListener('click', cerrarFuera))

async function abrir(n) {
    const fallback = notificationPath(n)
    try {
        const destino = await api.getTarget(n.id)
        abierto.value = false
        await router.push(destino || fallback)
    } catch {
        abierto.value = false
        await router.push(fallback)
    }
}

async function eliminar(id) {
    error.value = ''
    try {
        await api.eliminarNotificacionUnseen(id)
        notificaciones.value = notificaciones.value.filter((item) => item.id !== id)
    } catch {
        error.value = 'No se pudo borrar la notificación.'
    }
}

async function limpiar() {
    error.value = ''
    try {
        await api.eliminarNotificacionesUnseen()
        notificaciones.value = []
    } catch {
        error.value = 'No se pudieron borrar las notificaciones.'
    }
}

async function marcarLeidas() {
    error.value = ''
    try {
        await api.marcarLeidas()
        const ahora = DateTime.utc().toISO()
        notificaciones.value = notificaciones.value.map((n) => ({ ...n, readAt: n.readAt || ahora }))
    } catch {
        error.value = 'No se pudieron marcar como leídas.'
    }
}
</script>

<template>
    <li class="dropdown notification-list" ref="dropdown">
        <button class="nav-link dropdown-toggle arrow-none border-0 bg-transparent" type="button"
            :aria-expanded="abierto" aria-label="Notificaciones" @click="abierto = !abierto">
            <i class="ri-notification-3-line font-22"></i>
            <span v-if="noLeidas" class="noti-icon-badge"></span>
        </button>
        <div class="dropdown-menu dropdown-menu-end notification-panel p-0" :class="{ show: abierto }">
            <div class="notification-panel__header">
                <div class="notification-panel__heading">Notificaciones <span class="notification-panel__count">{{ noLeidas }}</span></div>
                <div class="notification-panel__actions">
                    <button type="button" :disabled="!noLeidas" @click="marcarLeidas">Marcar leídas</button>
                    <button type="button" :disabled="!notificaciones.length" @click="limpiar">Limpiar</button>
                </div>
            </div>
            <div class="notification-panel__filters" aria-label="Filtrar notificaciones">
                <button v-for="option in [{ id: 'todas', label: 'Todas' }, { id: 'ventas', label: 'Ventas' }, { id: 'publicaciones', label: 'Publicaciones' }]"
                    :key="option.id" type="button" :class="{ active: filtro === option.id }" @click="filtro = option.id">{{ option.label }}</button>
            </div>
            <p v-if="error" class="notification-panel__error" role="alert">{{ error }}</p>
            <div class="notification-panel__scroll">
                <template v-for="grupo in [{ label: 'HOY', items: hoy }, { label: 'ANTERIORES', items: anteriores }]" :key="grupo.label">
                    <div v-if="grupo.items.length" class="notification-panel__group">{{ grupo.label }}</div>
                    <div v-for="n in grupo.items" :key="n.id" class="notification-panel__item" :class="{ 'notification-panel__item--unread': !n.readAt }">
                        <span class="notification-panel__icon" :class="{ 'notification-panel__icon--sale': /order|payment/.test(n.topic ?? '') }">
                            <i :class="notificationIcon(n)"></i>
                        </span>
                        <div class="notification-panel__text">
                            <a href="#" class="notification-panel__title" @click.prevent="abrir(n)">
                                {{ notificationTitle(n) }} <span v-if="!n.readAt" class="notification-panel__dot"></span>
                            </a>
                            <p v-if="notificationBody(n)" class="notification-panel__body" :title="notificationBody(n)">{{ notificationBody(n) }}</p>
                            <p v-else-if="notificationResource(n)" class="notification-panel__body">{{ notificationResource(n) }}</p>
                            <small>{{ notificationTime(n) }}</small>
                        </div>
                        <button type="button" class="notification-panel__close" aria-label="Borrar notificación"
                            @click="eliminar(n.id)"><i class="mdi mdi-close"></i></button>
                    </div>
                </template>
                <p v-if="!filtradas.length" class="notification-panel__empty">No hay notificaciones en este filtro.</p>
            </div>
            <router-link to="/notificaciones" class="notification-panel__footer" @click="abierto = false">Ver historial completo</router-link>
        </div>
    </li>
</template>

<style scoped>
.notification-panel { --notif-surface: #fff; --notif-border: #e5e7eb; --notif-text: #253044; position: absolute; right: 0; top: 72px; width: min(420px, calc(100vw - 1rem)); max-width: none; border: 1px solid var(--notif-border); border-radius: 12px; background: var(--notif-surface); color: var(--notif-text); box-shadow: 0 12px 30px rgb(0 0 0 / 12%); overflow: hidden; }
:global([data-bs-theme="dark"] .notification-panel) { --notif-surface: #37404a; --notif-border: #505d6b; --notif-text: #f1f4f7; }
.notification-panel__header { display: flex; align-items: center; justify-content: space-between; gap: .5rem; padding: 1rem; border-bottom: 1px solid var(--notif-border); }
.notification-panel__heading { font-weight: 700; white-space: nowrap; }
.notification-panel__count { display: inline-grid; place-items: center; min-width: 1.25rem; height: 1.25rem; border-radius: 50%; background: #191d23; color: #fff; font-size: .72rem; margin-left: .25rem; }
.notification-panel__actions { display: flex; gap: .65rem; }
.notification-panel__actions button { border: 0; background: transparent; color: var(--ct-primary, #295aa5); font-size: .75rem; white-space: nowrap; padding: 0; }
.notification-panel__actions button:last-child { color: var(--ct-gray-600, #67707c); }
.notification-panel__actions button:disabled { opacity: .45; }
.notification-panel__filters { display: flex; gap: .45rem; padding: .7rem 1rem; border-bottom: 1px solid var(--notif-border); }
.notification-panel__filters button { border: 1px solid var(--notif-border); background: transparent; color: inherit; border-radius: 999px; padding: .22rem .75rem; font-size: .75rem; }
.notification-panel__filters button.active { background: #191d23; border-color: #191d23; color: #fff; }
.notification-panel__scroll { max-height: 400px; overflow-y: auto; }
.notification-panel__group { padding: .4rem 1rem; background: var(--ct-gray-100, #f7f8fa); color: var(--ct-gray-600, #66707c); font-size: .65rem; font-weight: 700; letter-spacing: .04em; }
.notification-panel__item { display: flex; gap: .7rem; align-items: flex-start; padding: .9rem 1rem; border-bottom: 1px solid var(--notif-border); min-width: 0; }
.notification-panel__item--unread { background: color-mix(in srgb, var(--ct-primary, #2874d0) 4%, var(--notif-surface)); }
.notification-panel__icon { flex: 0 0 2rem; display: grid; place-items: center; width: 2rem; height: 2rem; border-radius: .5rem; background: #edf3fd; color: #23569e; }
.notification-panel__icon--sale { background: #e9f7f0; color: #187748; }
.notification-panel__text { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.notification-panel__title { color: inherit; font-size: .81rem; font-weight: 700; text-decoration: none; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; overflow-wrap: anywhere; }
.notification-panel__title:hover { color: var(--ct-primary, #295aa5); }
.notification-panel__dot { display: inline-block; width: .35rem; height: .35rem; border-radius: 50%; background: #2773d2; margin-left: .2rem; }
.notification-panel__body { margin: .15rem 0; color: var(--ct-gray-700, #526071); font-size: .75rem; line-height: 1.4; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; overflow-wrap: anywhere; }
.notification-panel__text small { color: var(--ct-gray-600, #687483); font-size: .69rem; }
.notification-panel__close { flex: 0 0 auto; border: 0; background: transparent; color: #a0a8b3; padding: 0; line-height: 1; }
.notification-panel__empty { text-align: center; color: var(--ct-gray-600, #687483); padding: 1.5rem; margin: 0; }
.notification-panel__error { color: #c43b3b; margin: .5rem 1rem; font-size: .75rem; }
.notification-panel__footer { display: block; text-align: center; text-decoration: none; color: inherit; font-size: .76rem; font-weight: 600; padding: .7rem; }
.notification-panel__footer:hover { background: var(--ct-gray-100, #f7f8fa); }
@media (max-width: 520px) {
  .notification-panel { position: fixed; left: .5rem; right: .5rem; top: 72px; width: auto; z-index: 1050; }
  .notification-panel__scroll { max-height: calc(100vh - 220px); }
}
</style>
